import { useState } from 'react';
import { Card, Input, Button } from 'components/ui';
import { ImageIcon, CloseIcon } from 'components/icons';

/**
 * A vendor product-request notification: edit price + quantity, then accept,
 * reject ("Don't have") or ignore.
 */
export function NotificationCard({ notification, onAccept, onReject, onIgnore, busy }) {
  const name = notification.productName || notification.title || 'Product request';
  const [price, setPrice] = useState(
    notification.suggestedPrice != null ? String(notification.suggestedPrice) : '',
  );
  const [qty, setQty] = useState(String(notification.productCount ?? 1));

  const changeQty = (delta) =>
    setQty((value) => String(Math.max(0, (parseInt(value, 10) || 0) + delta)));

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <p className="line-clamp-2 text-sm font-semibold text-slate-900">{name}</p>
        <button
          type="button"
          onClick={onIgnore}
          disabled={busy}
          aria-label="Ignore"
          className="shrink-0 rounded p-1 text-slate-400 transition hover:bg-surface-100 hover:text-slate-600"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex gap-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-surface-100">
          {notification.productImageUrl ? (
            <img
              src={notification.productImageUrl}
              alt={name}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="flex h-full items-center justify-center text-slate-300">
              <ImageIcon className="h-6 w-6" />
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-wrap items-end gap-4">
          <Input
            label="Your price"
            type="number"
            min="0"
            step="any"
            leftAddon="₹"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            containerClassName="w-36"
          />
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Quantity</span>
            <div className="inline-flex items-center rounded-lg border border-surface-300">
              <button
                type="button"
                onClick={() => changeQty(-1)}
                className="flex h-9 w-9 items-center justify-center text-lg text-slate-600 transition hover:bg-surface-100"
                aria-label="Decrease quantity"
              >
                &minus;
              </button>
              <input
                value={qty}
                onChange={(event) => setQty(event.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                aria-label="Quantity"
                className="h-9 w-12 border-x border-surface-300 text-center text-sm text-slate-900 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => changeQty(1)}
                className="flex h-9 w-9 items-center justify-center text-lg text-slate-600 transition hover:bg-surface-100"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {notification.body && <p className="mt-3 text-sm text-slate-600">{notification.body}</p>}

      <div className="mt-3 flex gap-2">
        <Button variant="secondary" fullWidth onClick={onReject} disabled={busy}>
          Don&apos;t have
        </Button>
        <Button fullWidth isLoading={busy} onClick={() => onAccept({ price, qty })}>
          Accept
        </Button>
      </div>
    </Card>
  );
}

export default NotificationCard;
